import { MeetingHrFeedbackResult, MeetingStatus, AppraisalMeetingType, MeetingPdfStatus } from '../../../types'
import { TFunction } from 'i18next'

export const appraisalTableUiProps = (
  translation: TFunction,
  dateShort?: (utcDateString: string) => string | undefined,
  dateTimeShort?: (utcDateString: string) => string | undefined,
): {
  mandatoryColor: (mandatory: boolean) => 'primary' | 'secondary-lighter'
  mandatoryIcon: (mandatory: boolean) => 'monetary_stroke' | 'monetaryNo_stroke'
  typeColor: (type: AppraisalMeetingType) => 'lime' | 'gold' | 'red'
  statusColor: (status: string) => 'primary' | 'secondary' | 'info' | 'success' | 'error'
  statusIcon: (status: string) => 'close_stroke' | 'invite_stroke' | 'waiting_stroke' | 'check_stroke'
  statusTooltip: (
    status: string,
    employeeFirstName: string,
    reviewerName?: string,
    appraisalDate?: string,
    reviewerUnknown?: boolean,
    hasFeedback?: boolean,
    hrResult?: MeetingHrFeedbackResult,
  ) => string
  feedbackBtnColor: (feedbackType: MeetingStatus, feedbackStatus: MeetingHrFeedbackResult) => 'secondary' | 'error' | 'warning'
  feedbackBtnTooltip: (feedbackType: MeetingStatus, feedbackStatus: string) => string
  feedbackColor: (feedbackType: MeetingStatus) => 'secondary' | 'error' | 'warning'
  feedbackHeading: (feedbackType: MeetingStatus, employeeFirstName: string) => string
  feedbackResultColor: (result: MeetingHrFeedbackResult) => 'success' | 'warning' | 'default'
  feedbackResultText: (result: MeetingHrFeedbackResult) => string
  pdfBtnTooltip: (status: MeetingPdfStatus) => string | undefined
} => {
  const t = translation
  const formatDateShort = dateShort
  const formatDateTimeShort = dateTimeShort

  const mandatoryColor = (mandatory: boolean): 'primary' | 'secondary-lighter' => {
    if (mandatory) {
      return 'primary'
    } else {
      return 'secondary-lighter'
    }
  }

  const mandatoryIcon = (mandatory: boolean): 'monetary_stroke' | 'monetaryNo_stroke' => {
    if (mandatory) {
      return 'monetary_stroke'
    } else {
      return 'monetaryNo_stroke'
    }
  }

  const typeColor = (type: AppraisalMeetingType): 'lime' | 'gold' | 'red' => {
    if (type === AppraisalMeetingType.Normal) {
      return 'lime'
    } else if (type === AppraisalMeetingType.SkippedSteps) {
      return 'gold'
    } else if (type === AppraisalMeetingType.ForcedTargets) {
      return 'red'
    }
    return 'lime' // falback
  }

  const statusIcon = (status: string): 'close_stroke' | 'invite_stroke' | 'waiting_stroke' | 'check_stroke' => {
    if (status === 'NotInvited') {
      return 'close_stroke'
    } else if (status === 'Invited') {
      return 'invite_stroke'
    } else if (status === 'Waiting') {
      return 'waiting_stroke'
    } else if (status === 'Confirmed' || status === 'DisputedConfirmed') {
      return 'check_stroke'
    } else {
      return 'close_stroke' // falback
    }
  }

  const statusColor = (status: string): 'primary' | 'secondary' | 'info' | 'success' | 'error' => {
    if (status === 'NotInvited') {
      return 'secondary'
    } else if (status === 'Invited') {
      return 'primary'
    } else if (status === 'Waiting') {
      return 'info'
    } else if (status === 'Confirmed') {
      return 'success'
    } else if (status === 'DisputedConfirmed') {
      return 'error'
    } else {
      return 'secondary'
    }
  }

  const statusTooltip = (
    status: string,
    employeeFirstName: string,
    reviewerName?: string,
    appraisalDate?: string,
    reviewerUnknown?: boolean,
    hasFeedback?: boolean,
    hrResult?: MeetingHrFeedbackResult,
  ) => {
    const renderReviewerName = reviewerUnknown ? t('user.unknown.name') : reviewerName ? reviewerName : ''
    const meetingWasOn = t('companySettingsView.appraisalTab.table.status.tooltip.meetingWasOn', {
      appraisalDate: appraisalDate ? formatDateShort && formatDateShort(appraisalDate) : '',
      reviewerName: renderReviewerName,
    })
    const renderText = (type: string, result?: string) =>
      t(`companySettingsView.appraisalTab.table.status.tooltip.${type}`, {
        employeeName: employeeFirstName,
        meetingWasOn: meetingWasOn,
        result: result && t(`companySettingsView.appraisalTab.table.status.tooltip.confirmedByHrResult.${result}`),
      })

    if (status === 'NotInvited') {
      return t('companySettingsView.appraisalTab.table.status.tooltip.notInvited', { employeeName: employeeFirstName })
    } else if (status === 'Invited') {
      return t('companySettingsView.appraisalTab.table.status.tooltip.invited', {
        reviewerName: renderReviewerName,
        employeeName: employeeFirstName,
        appraisalDate: appraisalDate ? formatDateTimeShort && formatDateTimeShort(appraisalDate) : '',
      })
    } else if (status === 'Waiting') {
      return renderText('waiting')
    } else if ((status === 'Confirmed' || status === 'DisputedConfirmed') && hrResult === MeetingHrFeedbackResult.AppraisalUpdate) {
      return renderText('confirmedByHr', 'appraisalUpdate')
    } else if ((status === 'Confirmed' || status === 'DisputedConfirmed') && hrResult === MeetingHrFeedbackResult.KeepingChanges) {
      return renderText('confirmedByHr', 'keepingChanges')
    } else if (status === 'Confirmed' && hasFeedback) {
      return renderText('confirmedFeedback')
    } else if (status === 'Confirmed') {
      return renderText('confirmed')
    } else if (status === 'DisputedConfirmed') {
      return renderText('confirmedDisputed')
    }
    return ''
  }

  const feedbackBtnColor = (feedbackType: MeetingStatus, feedbackStatus: MeetingHrFeedbackResult): 'secondary' | 'error' | 'warning' => {
    if (feedbackType === MeetingStatus.Confirmed && feedbackStatus === MeetingHrFeedbackResult.NoResult) {
      return 'warning'
    } else if (feedbackType === MeetingStatus.DisputedConfirmed && feedbackStatus === MeetingHrFeedbackResult.NoResult) {
      return 'error'
    } else if (
      (feedbackType === MeetingStatus.Confirmed || feedbackType === MeetingStatus.DisputedConfirmed) &&
      feedbackStatus !== MeetingHrFeedbackResult.NoResult
    ) {
      return 'secondary'
    }
    return 'error' // fallback
  }

  const feedbackBtnTooltip = (feedbackType: MeetingStatus, feedbackStatus: string): string => {
    if (feedbackType === MeetingStatus.Confirmed && feedbackStatus === 'open') {
      return t('companySettingsView.appraisalTab.table.feedback.tooltip.approved')
    } else if (feedbackType === MeetingStatus.DisputedConfirmed && feedbackStatus === 'open') {
      return t('companySettingsView.appraisalTab.table.feedback.tooltip.disputed')
    } else if (feedbackType === MeetingStatus.Confirmed && feedbackStatus === 'closed') {
      return t('companySettingsView.appraisalTab.table.feedback.tooltip.resolvedApproved')
    } else if (feedbackType === MeetingStatus.DisputedConfirmed && feedbackStatus === 'closed') {
      return t('companySettingsView.appraisalTab.table.feedback.tooltip.resolvedDisputed')
    }
    return t('companySettingsView.appraisalTab.table.feedback.tooltip.fallback') // fallback
  }

  const feedbackColor = (feedbackType: MeetingStatus): 'secondary' | 'error' | 'warning' => {
    if (feedbackType === MeetingStatus.Confirmed) {
      return 'warning'
    } else if (feedbackType === MeetingStatus.DisputedConfirmed) {
      return 'error'
    }
    return 'secondary' // fallback
  }

  const feedbackHeading = (feedbackType: MeetingStatus, employeeFirstName: string): string => {
    if (feedbackType === MeetingStatus.Confirmed) {
      return t('companySettingsView.appraisalTab.table.feedback.modal.feedback.approved', {
        employee: employeeFirstName,
      })
    } else if (feedbackType === MeetingStatus.DisputedConfirmed) {
      return t('companySettingsView.appraisalTab.table.feedback.modal.feedback.disputed', {
        employee: employeeFirstName,
      })
    }
    return t('companySettingsView.appraisalTab.table.feedback.modal.feedback.fallback', {
      employee: employeeFirstName,
    }) // fallback
  }

  const feedbackResultColor = (result: MeetingHrFeedbackResult): 'success' | 'warning' | 'default' => {
    if (result === MeetingHrFeedbackResult.KeepingChanges) {
      return 'success'
    } else if (result === MeetingHrFeedbackResult.AppraisalUpdate) {
      return 'warning'
    }
    return 'default' // fallback
  }

  const feedbackResultText = (result: MeetingHrFeedbackResult): string => {
    if (result === MeetingHrFeedbackResult.KeepingChanges) {
      return t('companySettingsView.appraisalTab.table.feedback.modal.result.view.option.keeping')
    } else if (result === MeetingHrFeedbackResult.AppraisalUpdate) {
      return t('companySettingsView.appraisalTab.table.feedback.modal.result.view.option.appraisalUpdateMeeting')
    }
    return ''
  }

  const pdfBtnTooltip = (status: MeetingPdfStatus): string | undefined => {
    if (status === MeetingPdfStatus.NotStarted) {
      return t('dashboard.employee.meetings.pdfGenerationNotStarted')
    } else if (status === MeetingPdfStatus.InProgress) {
      return t('dashboard.employee.meetings.pdfGenerationInProgress')
    } else if (status === MeetingPdfStatus.FinishedSuccess) {
      return t('companySettingsView.appraisalTab.table.actions.conclusionBtnTooltip')
    } else if (status === MeetingPdfStatus.FinishedError) {
      return t('dashboard.employee.meetings.pdfGenerationError')
    } else {
      return undefined // Fallback
    }
  }

  return {
    mandatoryColor,
    mandatoryIcon,
    typeColor,
    statusColor,
    statusIcon,
    statusTooltip,
    feedbackBtnColor,
    feedbackBtnTooltip,
    feedbackColor,
    feedbackHeading,
    feedbackResultColor,
    feedbackResultText,
    pdfBtnTooltip,
  }
}
